/**
 * Apple Pay Web Integration Types
 *
 * @see https://developer.apple.com/documentation/applepayontheweb
 * @see https://developer.apple.com/documentation/applepayontheweb/apple-pay-js-api
 */

/**
 * @deprecated Backend now provides payment request configuration.
 * Use serviceId with applePayService.getPaymentRequest(serviceId) instead.
 *
 * Configuration for Apple Pay web setup.
 * Note: merchantId is NOT included - backend handles it during merchant validation.
 */
export interface ApplePayConfig {
  /** Merchant display name shown on payment sheet */
  merchantName: string;
  /** ISO 3166-1 alpha-2 country code (e.g., 'AU') */
  countryCode: string;
  /** ISO 4217 currency code (e.g., 'AUD') */
  currencyCode: string;
  /** Domain where payment is initiated (e.g., 'www.bottlemart.com.au') */
  merchantDomain: string;
  /** Endpoint for merchant validation (server-side) */
  merchantValidationEndpoint: string;
  /** Supported card networks */
  supportedNetworks: ApplePaySupportedNetwork[];
  /** Merchant capabilities */
  merchantCapabilities: ApplePayMerchantCapability[];
}

/**
 * Supported Apple Pay card networks
 */
export type ApplePaySupportedNetwork =
  | 'visa'
  | 'masterCard'
  | 'amex'
  | 'discover'
  | 'jcb'
  | 'chinaUnionPay'
  | 'eftpos'
  | 'electron'
  | 'maestro'
  | 'vPay';

/**
 * Apple Pay merchant capabilities
 */
export type ApplePayMerchantCapability =
  | 'supports3DS'
  | 'supportsCredit'
  | 'supportsDebit'
  | 'supportsEMV';

/**
 * Apple Pay payment request structure
 */
export interface ApplePayPaymentRequest {
  countryCode: string;
  currencyCode: string;
  supportedNetworks: ApplePaySupportedNetwork[];
  merchantCapabilities: ApplePayMerchantCapability[];
  total: ApplePayLineItem;
  lineItems?: ApplePayLineItem[];
  requiredBillingContactFields?: ApplePayContactField[];
  requiredShippingContactFields?: ApplePayContactField[];
  shippingMethods?: ApplePayShippingMethod[];
  shippingType?: 'shipping' | 'delivery' | 'storePickup' | 'servicePickup';
  applicationData?: string;
}

/**
 * Line item for Apple Pay payment sheet
 */
export interface ApplePayLineItem {
  label: string;
  amount: string; // Decimal string (e.g., '10.00')
  type?: 'final' | 'pending';
}

/**
 * Contact fields that can be requested
 */
export type ApplePayContactField = 'email' | 'name' | 'phone' | 'postalAddress' | 'phoneticName';

/**
 * Shipping method option
 */
export interface ApplePayShippingMethod {
  label: string;
  detail: string;
  amount: string;
  identifier: string;
}

/**
 * Apple Pay payment token structure
 */
export interface ApplePayPaymentToken {
  paymentMethod: ApplePayPaymentMethod;
  transactionIdentifier: string;
  paymentData: ApplePayPaymentData;
}

/**
 * Payment method info from the token
 */
export interface ApplePayPaymentMethod {
  displayName: string;
  network: string;
  type: 'debit' | 'credit' | 'prepaid' | 'store';
  paymentPass?: {
    primaryAccountIdentifier: string;
    primaryAccountNumberSuffix: string;
  };
}

/**
 * Encrypted payment data from Apple Pay
 * This should be sent to your payment processor (Till Payments)
 */
export interface ApplePayPaymentData {
  /** Base64-encoded encrypted payment data */
  data: string;
  /** Signature for verification */
  signature: string;
  /** Header containing ephemeral public key and transaction ID */
  header: {
    ephemeralPublicKey: string;
    publicKeyHash: string;
    transactionId: string;
  };
  /** Payment data version */
  version: string;
}

/**
 * Apple Pay payment result containing contact info and token
 */
export interface ApplePayPayment {
  token: ApplePayPaymentToken;
  billingContact?: ApplePayContact;
  shippingContact?: ApplePayContact;
  shippingMethod?: ApplePayShippingMethod;
}

/**
 * Contact information structure
 * Supports both flat (web ApplePaySession) and nested (iOS native) formats
 */
export interface ApplePayContact {
  phoneNumber?: string;
  emailAddress?: string;
  givenName?: string;
  familyName?: string;
  phoneticGivenName?: string;
  phoneticFamilyName?: string;
  // Flat format (web ApplePaySession)
  addressLines?: string[];
  locality?: string;
  subLocality?: string;
  administrativeArea?: string;
  subAdministrativeArea?: string;
  postalCode?: string;
  countryCode?: string;
  // Nested format (iOS native via @rnw-community/react-native-payments)
  postalAddress?: ApplePayPostalAddress;
}

/**
 * Postal address structure for iOS native responses
 */
export interface ApplePayPostalAddress {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
}

/**
 * Merchant session data returned from Apple Pay servers via backend
 */
export interface ApplePayMerchantSessionData {
  epochTimestamp: number;
  expiresAt: number;
  merchantSessionIdentifier: string;
  nonce: string;
  merchantIdentifier: string;
  domainName: string;
  displayName: string;
  signature: string;
}

/**
 * Merchant session returned from Apple Pay servers
 * This is opaque and must be passed to completeMerchantValidation
 */
export type ApplePayMerchantSession = ApplePayMerchantSessionData | Record<string, unknown>;

/**
 * Apple Pay session events
 */
export type ApplePaySessionEvent =
  | 'validatemerchant'
  | 'paymentmethodselected'
  | 'shippingmethodselected'
  | 'shippingcontactselected'
  | 'paymentauthorized'
  | 'cancel';

/**
 * Status codes for payment completion
 */
export enum ApplePayStatus {
  SUCCESS = 0,
  FAILURE = 1,
  INVALID_BILLING_ADDRESS = 2,
  INVALID_SHIPPING_ADDRESS = 3,
  INVALID_SHIPPING_METHOD = 4,
  PIN_REQUIRED = 5,
  PIN_INCORRECT = 6,
  PIN_LOCKOUT = 7,
}

/**
 * Apple Pay availability state
 */
export interface ApplePayAvailability {
  /** Whether Apple Pay JS is supported in this browser */
  isSupported: boolean;
  /** Whether the device can make Apple Pay payments */
  canMakePayments: boolean;
  /** Whether user has an active card set up */
  canMakePaymentsWithActiveCard: boolean;
  /** Payment credential status from applePayCapabilities() - iOS 18+ third-party browser support */
  paymentCredentialStatus?: ApplePayCredentialStatus;
  /** Error message if availability check failed */
  error?: string;
}

/**
 * Payment credential status from applePayCapabilities() API
 * Used for third-party browser support (Chrome, Firefox, Edge) with iOS 18+
 *
 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaycapabilities
 */
export type ApplePayCredentialStatus =
  | 'paymentCredentialsAvailable' // User has cards set up - show button prominently
  | 'paymentCredentialStatusUnknown' // Unknown status - show as payment option
  | 'paymentCredentialsUnavailable' // No cards set up - optionally show button
  | 'applePayUnsupported'; // Apple Pay not supported - hide button

/**
 * Result from applePayCapabilities() API
 */
export interface ApplePayCapabilitiesResult {
  paymentCredentialStatus: ApplePayCredentialStatus;
}

/**
 * Apple Pay session state for React hook
 */
export interface ApplePaySessionState {
  /** Whether a payment session is active */
  isActive: boolean;
  /** Whether merchant validation is in progress */
  isValidating: boolean;
  /** Whether payment authorization is in progress */
  isAuthorizing: boolean;
  /** Current error if any */
  error: string | null;
}

/**
 * @deprecated ApplePayButton now uses serviceId-based props.
 * Props are defined in ApplePayButton.web.tsx.
 *
 * Old props for Apple Pay button component (Web - Apple Pay JS)
 */
export interface ApplePayButtonProps {
  /** Apple Pay configuration */
  config: ApplePayConfig;
  /** Total amount to charge */
  amount: string;
  /** Line items to show on payment sheet */
  lineItems?: ApplePayLineItem[];
  /** Callback when payment is authorized */
  onPaymentAuthorized: (payment: ApplePayPayment) => Promise<boolean>;
  /** Callback for merchant validation (server-side) */
  onMerchantValidation: (validationURL: string) => Promise<ApplePayMerchantSession>;
  /** Callback when payment is cancelled */
  onCancel?: () => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Button style variant */
  buttonStyle?: 'black' | 'white' | 'white-outline';
  /** Button type - determines the button label text */
  buttonType?: 'plain' | 'buy' | 'pay' | 'set-up' | 'donate' | 'check-out' | 'book' | 'subscribe';
  /** Whether button is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Props for Apple Pay button component (iOS Native - PassKit)
 */
export interface ApplePayButtonIOSProps {
  /** Apple Pay service ID from usePaymentServices */
  serviceId: string;
  /** Remaining balance in cents after gift cards (for split payment) */
  remainingBalanceCents?: number;
  /** Total amount in cents */
  totalAmount: number;
  /** Current order with line items and adjustments */
  currentOrder: {
    order_lines?: Array<{
      charge?: boolean;
      name?: string;
      subtotal?: {cents_inc_tax: number};
    }>;
    adjustments?: Array<{
      name?: string;
      amount?: {cents_inc_tax: number};
    }>;
  };
  /** Whether this is a delivery order */
  isDelivery: boolean;
  /** Callback when payment is authorized */
  onPaymentAuthorized: (payment: ApplePayPayment) => Promise<boolean>;
  /** Callback when payment is cancelled */
  onCancel: () => void;
  /** Callback for errors */
  onError: (error: Error) => void;
  /** Whether button is disabled */
  disabled?: boolean;
}

/**
 * Global ApplePaySession interface (browser API)
 * Note: <apple-pay-button> custom element types are in /frontend/apple-pay.d.ts
 */
declare global {
  interface Window {
    ApplePaySession?: typeof ApplePaySession;
  }

  class ApplePaySession {
    static readonly STATUS_SUCCESS: number;
    static readonly STATUS_FAILURE: number;
    static readonly STATUS_INVALID_BILLING_POSTAL_ADDRESS: number;
    static readonly STATUS_INVALID_SHIPPING_POSTAL_ADDRESS: number;
    static readonly STATUS_INVALID_SHIPPING_CONTACT: number;
    static readonly STATUS_PIN_REQUIRED: number;
    static readonly STATUS_PIN_INCORRECT: number;
    static readonly STATUS_PIN_LOCKOUT: number;

    static supportsVersion(version: number): boolean;
    static canMakePayments(): boolean;
    static canMakePaymentsWithActiveCard(merchantIdentifier: string): Promise<boolean>;
    /**
     * Check Apple Pay capabilities for third-party browsers (iOS 18+)
     * Available in Apple Pay JS SDK 1.2.0+
     * @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaycapabilities
     */
    static applePayCapabilities?(): Promise<ApplePayCapabilitiesResult>;

    constructor(version: number, paymentRequest: ApplePayPaymentRequest);

    begin(): void;
    abort(): void;
    completeMerchantValidation(merchantSession: ApplePayMerchantSession): void;
    completePayment(result: {status: number}): void;
    completePaymentMethodSelection(update: {newTotal: ApplePayLineItem}): void;
    completeShippingMethodSelection(update: {newTotal: ApplePayLineItem}): void;
    completeShippingContactSelection(update: {
      newTotal: ApplePayLineItem;
      newShippingMethods?: ApplePayShippingMethod[];
      errors?: unknown[];
    }): void;

    onvalidatemerchant: ((event: {validationURL: string}) => void) | null;
    onpaymentauthorized: ((event: {payment: ApplePayPayment}) => void) | null;
    onpaymentmethodselected: ((event: {paymentMethod: ApplePayPaymentMethod}) => void) | null;
    onshippingmethodselected: ((event: {shippingMethod: ApplePayShippingMethod}) => void) | null;
    onshippingcontactselected: ((event: {shippingContact: ApplePayContact}) => void) | null;
    oncancel: ((event: Event) => void) | null;
  }
}

export {};
